import { DeleteOutlined } from '@ant-design/icons';
import { ProductInventoryDoc, PurchaseOrderSectionConfigDoc } from '@gooduncles/gu-app-schema';
import { Button, List, Modal, Select, Space, message } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';

import { errorObjectToString } from 'src/utils/common';
import { updatePurchaseOrderSectionConfig } from 'src/utils/firebase/firebase-short-cut';

import useProductInventoriesForSupplier from 'src/hooks/subscription/useProductInventoriesForSupplier';

interface InventoryLinkModalProps {
  open: boolean;
  onClose: () => void;
  config: PurchaseOrderSectionConfigDoc | null;
}

const InventoryLinkModal: FC<InventoryLinkModalProps> = ({ open, onClose, config }) => {
  const [linkedInventoryIds, setLinkedInventoryIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { productInventories, isLoading: isInventoriesLoading } = useProductInventoriesForSupplier(config?.supplierId);

  // config가 변경될 때 연결된 재고 ID 목록 초기화
  useEffect(() => {
    if (open && config) {
      setLinkedInventoryIds(config.inventoryIds ?? []);
    }
  }, [open, config]);

  // 재고 ID -> 재고 정보 맵
  const inventoryMap = useMemo(() => {
    const map = new Map<string, ProductInventoryDoc>();
    productInventories?.forEach((inventory) => {
      map.set(inventory._id, inventory);
    });
    return map;
  }, [productInventories]);

  // 선택 가능한 재고 옵션 (이미 연결된 것 제외)
  const availableOptions = useMemo(() => {
    return (
      productInventories
        ?.filter((inventory) => !linkedInventoryIds.includes(inventory._id))
        .map((inventory) => ({
          label: inventory.inventoryName,
          value: inventory._id,
        })) ?? []
    );
  }, [productInventories, linkedInventoryIds]);

  // 연결된 재고 목록 (재고 정보 포함)
  const linkedInventories = useMemo(() => {
    return linkedInventoryIds
      .map((id) => inventoryMap.get(id))
      .filter((inv): inv is ProductInventoryDoc => inv !== undefined);
  }, [linkedInventoryIds, inventoryMap]);

  const handleAddInventory = (inventoryId: string) => {
    if (!linkedInventoryIds.includes(inventoryId)) {
      setLinkedInventoryIds([...linkedInventoryIds, inventoryId]);
    }
  };

  const handleRemoveInventory = (inventoryId: string) => {
    setLinkedInventoryIds(linkedInventoryIds.filter((id) => id !== inventoryId));
  };

  const handleCancel = () => {
    setLinkedInventoryIds([]);
    onClose();
  };

  const handleSave = async () => {
    if (!config) return;

    try {
      setIsLoading(true);
      await updatePurchaseOrderSectionConfig(config._id, {
        inventoryIds: linkedInventoryIds,
      });
      message.success('연결된 재고가 저장되었습니다.');
      onClose();
    } catch (error) {
      const errorMessage = errorObjectToString(error);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title='연결된 재고 편집'
      open={open}
      centered
      onCancel={handleCancel}
      onOk={handleSave}
      okText='저장'
      cancelText='취소'
      width={600}
      confirmLoading={isLoading}
      okButtonProps={{ loading: isLoading }}
      cancelButtonProps={{ disabled: isLoading }}
    >
      <Space direction='vertical' style={{ width: '100%' }} size='middle'>
        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>재고 추가</div>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder='추가할 재고를 검색하세요'
            options={availableOptions}
            loading={isInventoriesLoading}
            value={null}
            onChange={handleAddInventory}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>연결된 재고 ({linkedInventories.length}개)</div>
          {linkedInventories.length === 0 ? (
            <div style={{ color: 'var(--gray500)', padding: '16px 0', textAlign: 'center' }}>
              연결된 재고가 없습니다.
            </div>
          ) : (
            <List
              size='small'
              bordered
              dataSource={linkedInventories}
              style={{ maxHeight: 300, overflow: 'auto' }}
              renderItem={(inventory) => (
                <List.Item
                  actions={[
                    <Button
                      key='delete'
                      type='text'
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveInventory(inventory._id)}
                    />,
                  ]}
                >
                  {inventory.inventoryName}
                </List.Item>
              )}
            />
          )}
        </div>
      </Space>
    </Modal>
  );
};

export default InventoryLinkModal;
